// VsmCanvas — Lean Value Stream Mapping view for one process at a time.
// Process dropdown, a left-to-right strip of nodes coloured by the
// referenced flow statement's type, a metric tile (CT · WT · %C&A) under
// each node, and a PCE chip in the header. The CT/WT/%C&A values are
// inline-editable — click to swap into an input, Enter or blur to save,
// Escape to cancel. Edits flow through the saveVsm callback into the
// server-side vsm blob; the canvas does its own optimistic local update
// via the same callback wiring in useSession.

import React, { useMemo, useState } from 'react';
import { parseFlow } from '../utils/flowParser';
import { computePce, humanizeSeconds, parseDurationSeconds } from '../utils/vsmModel';
import type { NewModel, Statement, Vsm, VsmStep } from '../utils/flowModel';
import '../styles/VsmCanvas.css';

interface Props {
  newModel: NewModel;
  onSaveVsm: (vsm: Vsm) => Promise<{ ok: boolean; error?: string }>;
  selectedProcessId?: string | null;
  onSelectProcess?: (processId: string) => void;
}

type EditField = 'ct' | 'wt' | 'ca';
interface EditState {
  processId: string;
  stepIndex: number;
  field: EditField;
}

const VsmCanvas: React.FC<Props> = ({
  newModel,
  onSaveVsm,
  selectedProcessId,
  onSelectProcess,
}) => {
  // Wrapped in useMemo so the `?? []` fallback doesn't return a fresh
  // array on every render and invalidate the downstream useMemo deps.
  const processes = useMemo(() => newModel.vsm?.processes ?? [], [newModel.vsm]);
  const [selectedId, setSelectedId] = useState<string | null>(processes[0]?.id ?? null);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const effectiveSelectedId = selectedProcessId !== undefined ? selectedProcessId : selectedId;

  // Keep the dropdown selection valid when the AI adds/removes processes.
  const selected = useMemo(() => {
    if (!processes.length) return null;
    const match = processes.find((p) => p.id === effectiveSelectedId);
    return match ?? processes[0];
  }, [processes, effectiveSelectedId]);

  // Parse the flow once so we can look up statement types/labels by id.
  const statementsById = useMemo(() => {
    const flow = parseFlow(newModel.flowText || '');
    const map = new Map<number, Statement>();
    for (const s of flow.statements) map.set(s.id, s);
    return map;
  }, [newModel.flowText]);

  // Hoist totals above the empty-state early return — rules-of-hooks.
  const selectedSteps = useMemo(() => selected?.steps ?? [], [selected]);
  const totals = useMemo(() => {
    let ct = 0;
    let wt = 0;
    for (const s of selectedSteps) {
      ct += parseDurationSeconds(s.ct);
      wt += parseDurationSeconds(s.wt);
    }
    return { ct, wt, lead: ct + wt };
  }, [selectedSteps]);

  const commitEdit = async (rawValue: string) => {
    if (!editing) return;
    const next: Vsm = {
      processes: processes.map((p) => {
        if (p.id !== editing.processId) return p;
        return {
          ...p,
          steps: p.steps.map((s, i) => {
            if (i !== editing.stepIndex) return s;
            if (editing.field === 'ca') {
              const n = Number(rawValue);
              const clamped = Number.isFinite(n)
                ? Math.max(0, Math.min(100, Math.round(n)))
                : s.ca;
              return { ...s, ca: clamped };
            }
            // CT and WT are stored as the raw human string; the canvas
            // re-parses on render. Trim and default empty to "0" so an
            // accidental clear doesn't produce a blank metric.
            const value = rawValue.trim() || '0';
            return { ...s, [editing.field]: value };
          }),
        };
      }),
    };
    setEditing(null);
    setSaveError(null);
    const res = await onSaveVsm(next);
    if (!res.ok) setSaveError(res.error || 'Save failed');
  };

  if (!processes.length) {
    return (
      <div className="vsm-empty">
        <div className="vsm-empty-icon">⏱</div>
        <div className="vsm-empty-title">No timing captured yet</div>
        <div className="vsm-empty-sub">
          Tell the facilitator how long the steps take (e.g. "the agent
          spends about ten minutes entering the order") or click the
          stopwatch on a Flow card to add the step manually.
        </div>
      </div>
    );
  }

  const steps = selectedSteps;
  const pce = steps.length ? computePce(steps) : null;

  return (
    <div className="vsm-host">
      <div className="vsm-header">
        <div className="vsm-process-picker">
          <label className="vsm-process-label" htmlFor="vsm-process-select">
            Process
          </label>
          <select
            id="vsm-process-select"
            className="vsm-process-select"
            value={selected?.id ?? ''}
            onChange={(e) => {
              if (selectedProcessId === undefined) setSelectedId(e.target.value);
              onSelectProcess?.(e.target.value);
            }}
          >
            {processes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div className="vsm-totals">
          {steps.length > 0 && (
            <>
              <div className="vsm-total-chip vsm-total-chip--lt" title="Lead Time — total elapsed Σ(CT + WT)">
                <span className="vsm-total-label">LT</span>
                <span className="vsm-total-value">{humanizeSeconds(totals.lead)}</span>
              </div>
              <div className="vsm-total-chip vsm-total-chip--va" title="Value-Added time — Σ(CT), the hands-on work">
                <span className="vsm-total-label">VA</span>
                <span className="vsm-total-value">{humanizeSeconds(totals.ct)}</span>
              </div>
            </>
          )}
          <div className="vsm-pce-chip" title="Process Cycle Efficiency = ΣCT / Σ(CT + WT)">
            <span className="vsm-pce-label">PCE</span>
            <span className="vsm-pce-value">{pce === null ? '—' : `${pce}%`}</span>
          </div>
        </div>
      </div>

      {saveError && (
        <div className="vsm-save-error" role="alert">
          Couldn't save: {saveError}
          <button type="button" className="vsm-save-error-dismiss" onClick={() => setSaveError(null)}>×</button>
        </div>
      )}

      <div className="vsm-strip-host">
        {steps.length === 0 ? (
          <div className="vsm-empty-line">
            No steps captured for this process. Use the stopwatch on any Flow card to add one.
          </div>
        ) : (
          <>
            <div className="vsm-strip">
              {steps.map((step, i) => (
                <React.Fragment key={`${step.statementId}-${i}`}>
                  {i > 0 && <InventoryTriangle />}
                  <VsmStepCard
                    step={step}
                    statement={statementsById.get(step.statementId) ?? null}
                    editingField={
                      editing && selected && editing.processId === selected.id && editing.stepIndex === i
                        ? editing.field
                        : null
                    }
                    onStartEdit={(field) => {
                      if (!selected) return;
                      setEditing({ processId: selected.id, stepIndex: i, field });
                    }}
                    onCommit={commitEdit}
                    onCancel={() => setEditing(null)}
                  />
                </React.Fragment>
              ))}
            </div>
            <VsmTimeline steps={steps} totals={totals} />
          </>
        )}
      </div>
    </div>
  );
};

export default VsmCanvas;

interface StepCardProps {
  step: VsmStep;
  statement: Statement | null;
  editingField: EditField | null;
  onStartEdit: (field: EditField) => void;
  onCommit: (rawValue: string) => void;
  onCancel: () => void;
}

// Inventory marker — the classic "I" triangle that VSMs put in the gaps
// between process boxes to call out WIP queueing there. Purely visual;
// the actual queue duration is in the WT metric on the next card.
function InventoryTriangle() {
  return (
    <div className="vsm-inv" title="Work-in-progress queue">
      <svg viewBox="0 0 28 32" width={28} height={32} aria-hidden>
        <polygon
          points="14,2 26,28 2,28"
          fill="none"
          stroke="#64748b"
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        <text
          x={14}
          y={24}
          textAnchor="middle"
          fontSize={12}
          fontStyle="italic"
          fontFamily="ui-serif, Georgia, serif"
          fill="#475569"
        >
          I
        </text>
      </svg>
    </div>
  );
}

// Classic VSM sawtooth: valleys sit directly under each step card and
// peaks ride the inter-card gap where the inventory triangle lives, so
// the timeline reads top-to-bottom as "wait queues here, then this step
// runs". Widths match the strip exactly — card width for valleys, gap
// width for peaks — so the bottom track visually aligns with the cards
// above it. When WT is 0 we keep the line flat across the gap and skip
// the label, which cleans up the noisy "0" peaks at the entry/exit of
// most software processes.
interface TimelineProps {
  steps: VsmStep[];
  totals: { ct: number; wt: number; lead: number };
}

// Must mirror the .vsm-step (CARD_W) and .vsm-strip gap + .vsm-inv
// (GAP_W) sizes in VsmCanvas.css. Drift here means the timeline
// detaches from the cards above it.
const TIMELINE_CARD_W = 200;
const TIMELINE_GAP_W = 52;
const TIMELINE_TOP_Y = 12;
const TIMELINE_BOT_Y = 56;
const TIMELINE_HEIGHT = 96;
const TIMELINE_LABEL_OFFSET = 6;

function VsmTimeline({ steps, totals }: TimelineProps) {
  const totalWidth = steps.length * TIMELINE_CARD_W
    + Math.max(0, steps.length - 1) * TIMELINE_GAP_W;

  type Pt = [number, number];
  type Label = { x: number; y: number; text: string; kind: 'wt' | 'ct' };
  const points: Pt[] = [];
  const labels: Label[] = [];
  let x = 0;
  points.push([x, TIMELINE_BOT_Y]);
  for (let i = 0; i < steps.length; i++) {
    if (i > 0) {
      const gapEnd = x + TIMELINE_GAP_W;
      const wtSec = parseDurationSeconds(steps[i].wt);
      if (wtSec > 0) {
        points.push([x, TIMELINE_TOP_Y]);
        points.push([gapEnd, TIMELINE_TOP_Y]);
        points.push([gapEnd, TIMELINE_BOT_Y]);
        labels.push({
          x: x + TIMELINE_GAP_W / 2,
          y: TIMELINE_TOP_Y - TIMELINE_LABEL_OFFSET,
          text: steps[i].wt,
          kind: 'wt',
        });
      } else {
        points.push([gapEnd, TIMELINE_BOT_Y]);
      }
      x = gapEnd;
    }
    const valleyEnd = x + TIMELINE_CARD_W;
    points.push([valleyEnd, TIMELINE_BOT_Y]);
    labels.push({
      x: x + TIMELINE_CARD_W / 2,
      y: TIMELINE_BOT_Y + TIMELINE_LABEL_OFFSET + 12,
      text: steps[i].ct,
      kind: 'ct',
    });
    x = valleyEnd;
  }

  return (
    <div className="vsm-timeline-wrap">
      <svg
        className="vsm-timeline"
        width={totalWidth}
        height={TIMELINE_HEIGHT}
        viewBox={`0 0 ${totalWidth} ${TIMELINE_HEIGHT}`}
        role="img"
        aria-label="Value Stream Map timeline"
      >
        <polyline
          points={points.map((p) => p.join(',')).join(' ')}
          fill="none"
          stroke="#475569"
          strokeWidth={1.5}
          strokeLinejoin="miter"
        />
        {labels.map((label, i) => (
          <text
            key={`tl-label-${i}`}
            x={label.x}
            y={label.y}
            textAnchor="middle"
            className={label.kind === 'wt' ? 'vsm-timeline-wt' : 'vsm-timeline-ct'}
          >
            {label.text}
          </text>
        ))}
      </svg>
      <div className="vsm-timeline-totals" aria-hidden>
        <div className="vsm-timeline-total">
          <span className="vsm-timeline-total-key">LT</span>
          <span className="vsm-timeline-total-val">{humanizeSeconds(totals.lead)}</span>
        </div>
        <div className="vsm-timeline-total">
          <span className="vsm-timeline-total-key">VA</span>
          <span className="vsm-timeline-total-val">{humanizeSeconds(totals.ct)}</span>
        </div>
      </div>
    </div>
  );
}

function VsmStepCard({ step, statement, editingField, onStartEdit, onCommit, onCancel }: StepCardProps) {
  const kind = statement?.type ?? 'unknown';
  const title = statement?.name?.trim() || `[${step.statementId}]`;

  return (
    <div className="vsm-step">
      <div className={`vsm-step-card vsm-step-card-${kind}`}>
        <div className="vsm-step-head">
          <span className="vsm-step-kind">{kind}</span>
          <span className="vsm-step-id">[{step.statementId}]</span>
        </div>
        <div className="vsm-step-name" title={title}>{title}</div>
      </div>
      <div className="vsm-metric-tile">
        <EditableMetric
          label="CT"
          value={step.ct}
          editing={editingField === 'ct'}
          onStartEdit={() => onStartEdit('ct')}
          onCommit={onCommit}
          onCancel={onCancel}
        />
        <EditableMetric
          label="WT"
          value={step.wt}
          editing={editingField === 'wt'}
          onStartEdit={() => onStartEdit('wt')}
          onCommit={onCommit}
          onCancel={onCancel}
        />
        <EditableMetric
          label="%C&A"
          value={String(step.ca)}
          editing={editingField === 'ca'}
          onStartEdit={() => onStartEdit('ca')}
          onCommit={onCommit}
          onCancel={onCancel}
          inputMode="numeric"
        />
      </div>
    </div>
  );
}

interface EditableMetricProps {
  label: string;
  value: string;
  editing: boolean;
  onStartEdit: () => void;
  onCommit: (rawValue: string) => void;
  onCancel: () => void;
  inputMode?: 'text' | 'numeric';
}

function EditableMetric({ label, value, editing, onStartEdit, onCommit, onCancel, inputMode = 'text' }: EditableMetricProps) {
  return (
    <span className="vsm-metric">
      <span className="vsm-metric-key">{label}</span>{' '}
      {editing ? (
        <input
          className="vsm-metric-input"
          autoFocus
          defaultValue={value}
          inputMode={inputMode}
          onBlur={(e) => onCommit(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onCommit(e.currentTarget.value);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancel();
            }
          }}
          size={Math.max(3, value.length + 1)}
        />
      ) : (
        <button
          type="button"
          className="vsm-metric-value"
          onClick={onStartEdit}
          title="Click to edit"
        >
          {value}
        </button>
      )}
    </span>
  );
}
